import React, { useState, useEffect } from "react";
import categoryService from "../../services/categoryService";

const CategoryForm = ({ category, onSubmit, onCancel, loading = false }) => {
  const [formData, setFormData] = useState({
    nameEn: "",
    nameLocal: "",
    parentCategoryId: "",
  });
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadCategories();
    if (category) {
      setFormData({
        nameEn: category.nameEn || "",
        nameLocal: category.nameLocal || "",
        parentCategoryId: category.parentCategoryId || "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const loadCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await categoryService.getAllCategories();
      if (response.success) {
        // Filter out current category and its children to prevent circular references
        const availableCategories = (response.data.data || []).filter((cat) => {
          if (category) {
            return (
              cat.id !== category.id && cat.parentCategoryId !== category.id
            );
          }
          return true;
        });
        setCategories(availableCategories);
      }
    } catch (error) {
      console.error("Error loading categories:", error);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nameEn.trim()) {
      newErrors.nameEn = "English name is required";
    }

    if (!formData.nameLocal.trim()) {
      newErrors.nameLocal = "Local name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const submitData = {
        nameEn: formData.nameEn.trim(),
        nameLocal: formData.nameLocal.trim(),
        parentCategoryId: formData.parentCategoryId || null,
      };
      onSubmit(submitData);
    }
  };

  // Get main categories (no parent) for the parent dropdown
  const mainCategories = categories.filter((cat) => !cat.parentCategoryId);

  return (
    <div className="category-form">
      <form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <div className="form-section">
          <div className="form-section-header">
            <i className="fas fa-info-circle"></i>
            <h6>Basic Information</h6>
          </div>

          <div className="row">
            <div className="col-md-6">
              <label className="form-label fw-medium">
                English Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${errors.nameEn ? "is-invalid" : ""}`}
                name="nameEn"
                value={formData.nameEn}
                onChange={handleInputChange}
                placeholder="Enter English name"
                disabled={loading}
              />
              {errors.nameEn && (
                <div className="invalid-feedback">{errors.nameEn}</div>
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label fw-medium">
                Local Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${
                  errors.nameLocal ? "is-invalid" : ""
                }`}
                name="nameLocal"
                value={formData.nameLocal}
                onChange={handleInputChange}
                placeholder="Enter local name"
                disabled={loading}
              />
              {errors.nameLocal && (
                <div className="invalid-feedback">{errors.nameLocal}</div>
              )}
            </div>
          </div>
        </div>

        {/* Category Hierarchy */}
        <div className="form-section">
          <div className="form-section-header">
            <i className="fas fa-sitemap"></i>
            <h6>Category Hierarchy</h6>
          </div>

          <div className="row">
            <div className="col-md-12">
              <label className="form-label fw-medium">Parent Category</label>
              <select
                className="form-select"
                name="parentCategoryId"
                value={formData.parentCategoryId}
                onChange={handleInputChange}
                disabled={loading || categoriesLoading}
              >
                <option value="">None (Main Category)</option>
                {mainCategories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
              <small className="form-text text-muted">
                Select a parent category to create a sub-category, or leave
                empty for a main category.
              </small>
            </div>
          </div>

          {formData.parentCategoryId && (
            <div className="alert alert-info mt-3">
              <i className="fas fa-info-circle me-2"></i>
              This will be created as a sub-category under{" "}
              <strong>
                {
                  mainCategories.find(
                    (cat) => cat.id.toString() === formData.parentCategoryId
                  )?.name
                }
              </strong>
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="d-flex justify-content-end gap-2 mt-4">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-success" disabled={loading}>
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                ></span>
                {category ? "Updating..." : "Creating..."}
              </>
            ) : (
              <>
                <i
                  className={`fas ${category ? "fa-save" : "fa-plus"} me-2`}
                ></i>
                {category ? "Update Category" : "Create Category"}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CategoryForm;
